// MACHINERY V2 (Stroje) — Fleet V2 row (M2.3).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.3. Reference impl:
// design_handoff_machinery_v3/machinery-v2-grouped.jsx (V2Row).
//
// Columns: [4px status edge] photo+capability badges | identity + badge + dot |
// location + S/N + system | live job cell | SR chip | kebab.
//
// SINGLE DERIVATION, SAME AS THE CARD. Everything status-shaped comes from
// deriveCardPresentation(telemetry): the edge colour, the badge, the dot
// (p.dotConn, never raw conn) and the job cell's branch. This component never
// re-tests alarms.length / status / conn. The artboard's own row DOES re-test
// them; porting that literally would have re-opened the split brain M2.1 closed.
//
// THE STATUS EDGE follows p.badgeStatus, so `unconnected` and `awaiting` get NO
// edge (the artboard's rule: an unconnected machine looks like today's list).
// `stale` with a null status — the TelemetryStale case — also gets no edge, for
// the same reason its badge is absent: we do not know the status, and inventing
// an edge colour would assert one.
//
// ROW NAVIGATION GUARD. The row is clickable, so every interactive descendant
// that could swallow a click stops propagation:
//   - the kebab trigger AND its portalled content (FleetCardMenu guards both)
//   - the capability badge (CapabilityBadge stops propagation before opening)
//   - the tooltip content (CapTip)
//   - the readiness popup is mounted at PAGE level, outside every row, and
//     additionally guards Dialog.Overlay AND Dialog.Content.
// Verified by reading the composed handler chain, not assumed.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import { getMachineDisplayName, getMachineName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { CapabilityBadges } from "../readiness/CapabilityBadge";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import ConnDot from "./ConnDot";
import FleetCardMenu, { FleetMenuAction } from "./FleetCardMenu";
import StatusBadge, { AwaitingBadge } from "./StatusBadge";
import { MachineryV2Telemetry, deriveCardPresentation } from "./cardStates";
import { statusColors } from "./cardStateStyles";
import { MachinePhoto, NewBadge, PinGlyph } from "./fleetCardAtoms";
import { FleetRowJobCell, FleetRowSrChip } from "./fleetRowCells";

export interface FleetRowProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  readiness: MachineryV2ReadinessPresentation | null;
  isNew: boolean;
  actions: FleetMenuAction[];
  onOpenProfile: () => void;
  onOpenReadiness: () => void;
}

const FleetRow: React.FC<FleetRowProps> = ({
  machine, telemetry, readiness, isNew, actions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const p = deriveCardPresentation(telemetry);
  const decommissioned = !!machine.isDecommissioned;

  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  const edge = p.badgeStatus ? statusColors(p.badgeStatus, p.staleLook).fg : null;
  const location = machine.locationName
    ? machine.parentLocationName
      ? machine.parentLocationName + " › " + machine.locationName
      : machine.locationName
    : null;

  return (
    <article
      onClick={onOpenProfile}
      data-machine-id={machine.id}
      style={{
        position: "relative", background: "var(--card)", borderRadius: 10,
        border: "1px solid var(--border)",
        borderLeft: edge ? "4px solid " + edge : "1px solid var(--border)",
        // Keep the CONTENT BOX identical either way, or the photo column jitters
        // as rows change state — and the un-edged case is `unconnected` /
        // `awaiting`, i.e. most of the fleet, so the jitter would be the norm.
        // Edged:    4px border + 11px padding = 15px
        // Un-edged: 1px border + 14px padding = 15px
        padding: edge ? "12px 14px 12px 11px" : "12px 14px",
        display: "grid",
        gridTemplateColumns:
          "120px minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.4fr) 84px 32px",
        gap: 16, alignItems: "center", minWidth: 0, cursor: "pointer",
        opacity: decommissioned ? 0.65 : 1,
      }}
    >
      <div style={{ position: "relative", flexShrink: 0 }}>
        <MachinePhoto
          src={images.length > 0 ? images[0].src : null}
          alt={getMachineDisplayName(machine)}
          grayscale={decommissioned}
          height={72}
        />
        {readiness && (
          <div style={{ position: "absolute", top: 4, left: 4 }}>
            <CapabilityBadges
              presentation={readiness}
              size={18}
              onOpenDetails={onOpenReadiness}
            />
          </div>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
            flexWrap: "wrap", minHeight: 22,
          }}
        >
          {p.showStatusRow ? (
            <>
              {p.showAwaitingBadge ? (
                <AwaitingBadge />
              ) : (
                <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
              )}
              <ConnDot state={p.dotConn} lastHeartbeat={telemetry.heartbeat} />
            </>
          ) : (
            <span
              style={{
                fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)",
                letterSpacing: "0.08em", textTransform: "uppercase",
              }}
            >
              {translate("myMachinery:v2.states.unconnected")}
            </span>
          )}
          {isNew && <NewBadge />}
        </div>
        <div
          style={{
            fontWeight: 600, fontSize: 14, color: "var(--ink)", lineHeight: 1.25,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {getMachineDisplayName(machine)}
        </div>
        <div
          style={{
            fontSize: 11.5, color: "var(--muted)", marginTop: 2, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {getMachineName(machine) || machine.type?.name || ""}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div className="eyebrow" style={{ marginBottom: 2 }}>
          {translate("myMachinery:locationFilter")}
        </div>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 5, fontSize: 12.5,
            color: "var(--ink-2)", minWidth: 0,
          }}
        >
          {location ? (
            <>
              <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>
                <PinGlyph />
              </span>
              <span
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {location}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--faint)", fontStyle: "italic" }}>
              {translate("myMachinery:locationNotSet")}
            </span>
          )}
        </div>
        <div
          style={{
            marginTop: 6, display: "flex", gap: 6, fontFamily: "var(--font-mono)",
            fontSize: 10.5, color: "var(--faint)", minWidth: 0,
          }}
        >
          <span style={{ whiteSpace: "nowrap" }}>S/N {machine.serialNumber || "—"}</span>
          <span style={{ color: "var(--border)" }}>·</span>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {machine.controlSystemBrand?.name || machine.system || "—"}
          </span>
        </div>
      </div>

      <FleetRowJobCell telemetry={telemetry} presentation={p} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <FleetRowSrChip machine={machine} />
      </div>

      <div style={{ justifySelf: "end" }}>
        <FleetCardMenu actions={actions} ariaLabel={translate("myMachinery:v2.cardMenu")} />
      </div>
    </article>
  );
};

export default FleetRow;
